import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { useSettings } from '../../context/SettingsContext';
import api from '../../utils/api';
import './Contact.css';

const SUBJECTS = [
  'Informacion i përgjithshëm',
  'Caktim takimi',
  'Ankesë',
  'Bashkëpunim',
  'Shtyp & Media',
  'Tjera',
];

export default function Contact() {
  const s = useSettings();
  const [form, setForm] = useState({ name:'', email:'', phone:'', subject:'', message:'' });
  const [loading,  setLoading]  = useState(false);
  const [success,  setSuccess]  = useState(false);
  const [error,    setError]    = useState('');

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await api.post('/contact', form);
      setSuccess(res.data.message);
      setForm({ name:'', email:'', phone:'', subject:'', message:'' });
    } catch (err) {
      setError(err.response?.data?.message || 'Gabim. Ju lutemi provoni përsëri.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />

      {/* Hero */}
      <div className="contact-hero">
        <div className="container">
          <span className="contact-eyebrow">Na kontaktoni</span>
          <h1 className="contact-hero-title">Kontakti</h1>
          <p className="contact-hero-sub">Jemi këtu për t'ju ndihmuar — na shkruani ose telefononi</p>
        </div>
      </div>

      <div className="container contact-page-body">

        {/* ── QUICK NUMBERS ──────────────────────── */}
        <div className="contact-quick-strip">
          <a href={`tel:${s.emergency_phone}`} className="cq-card emg">
            <Phone size={20}/>
            <div>
              <p className="cq-label">Emergjenca 24h</p>
              <p className="cq-value">{s.emergency_phone || '+383 45 104 230'}</p>
            </div>
          </a>
          <a href={`tel:${s.main_phone}`} className="cq-card">
            <Phone size={20}/>
            <div>
              <p className="cq-label">Drejtoria</p>
              <p className="cq-value">{s.main_phone || '+383 29 243 134'}</p>
            </div>
          </a>
          {/* <a href={`tel:${s.free_line}`} className="cq-card free"> */}
            {/* <Phone size={20}/> */}
            {/* <div> */}
              {/* <p className="cq-label">Linja falas</p> */}
              {/* <p className="cq-value">{s.free_line || '0800 17777'}</p> */}
            {/* </div> */}
          {/* </a> */}
          <a href={`mailto:${s.email}`} className="cq-card">
            <Mail size={20}/>
            <div>
              <p className="cq-label">Email</p>
              <p className="cq-value">{s.email || 'spitali.prizren@rks-gov.net'}</p>
            </div>
          </a>
        </div>

        {/* ── MAIN GRID: Form + Info ──────────────── */}
        <div className="contact-main-grid">

          {/* Contact Form */}
          <div className="contact-form-wrap card">
            <h2 className="contact-form-title">✉️ Dërgoni mesazh</h2>
            <p className="contact-form-sub">Plotësoni formularin dhe do t'ju kthejmë përgjigje brenda 24 orëve.</p>

            {success ? (
              <div className="contact-success">
                <CheckCircle size={40} color="#0A7B55"/>
                <h3>Mesazhi u dërgua!</h3>
                <p>{success}</p>
                <button className="btn btn-primary" style={{marginTop:16}} onClick={() => setSuccess(false)}>
                  Dërgo mesazh tjetër
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">Emri dhe Mbiemri *</label>
                    <input className="form-input" required placeholder="Emri juaj..."
                      value={form.name} onChange={set('name')}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input className="form-input" type="email" required placeholder="email@shembull.com"
                      value={form.email} onChange={set('email')}/>
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">Telefon</label>
                    <input className="form-input" placeholder="+383..."
                      value={form.phone} onChange={set('phone')}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subjekti</label>
                    <select className="form-input form-select" value={form.subject} onChange={set('subject')}>
                      <option value="">Zgjidh subjektin...</option>
                      {SUBJECTS.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Mesazhi *</label>
                  <textarea className="form-input" rows={6} required
                    placeholder="Shkruani mesazhin tuaj këtu..."
                    value={form.message} onChange={set('message')}/>
                </div>

                {error && <div className="contact-error">{error}</div>}

                <button type="submit" className="btn btn-primary contact-submit" disabled={loading}>
                  {loading ? 'Duke dërguar...' : <><Send size={16}/> Dërgo mesazhin</>}
                </button>
              </form>
            )}
          </div>

          {/* Info + Map */}
          <div className="contact-side">
            <div className="card contact-info-box">
              <h3 className="contact-info-title">📍 Informacione</h3>
              <div className="ci-rows">
                <div className="ci-row">
                  <MapPin size={18} className="ci-icon"/>
                  <div>
                    <p className="ci-label">Adresa</p>
                    <p className="ci-value">{s.address || 'Rruga Shkronjat, 20000 Prizren'}</p>
                  </div>
                </div>
                {s.working_hours && (
                  <div className="ci-row">
                    <Clock size={18} className="ci-icon"/>
                    <div>
                      <p className="ci-label">Orari i punës</p>
                      <p className="ci-value">{s.working_hours}</p>
                    </div>
                  </div>
                )}
                <div className="ci-row">
                  <Phone size={18} className="ci-icon"/>
                  <div>
                    <p className="ci-label">Fax / Drejtoria</p>
                    <p className="ci-value">{s.main_phone}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Map embed */}
            <div className="card contact-map-box">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2952.3!2d20.7399!3d42.2139!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1353a0ee4d8bb8e9%3A0x4fb5e3ba3b9e33ac!2sSpitali%20i%20P%C3%ABrgjithsh%C3%ABm%20i%20Prizrenit!5e0!3m2!1ssq!2sxk!4v1700000000000"
                title="Spitali i Prizrenit — Harta"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{border:0, width:'100%', height:'260px', display:'block'}}
              />
              <div className="map-link-bar">
                <a
                  href="https://maps.app.goo.gl/M4Gx4HwtvJG1gXvv6"
                  target="_blank" rel="noreferrer"
                  className="btn btn-secondary btn-sm"
                >
                  <MapPin size={13}/> Hap në Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
